/**
 * Profile.jsx — Personal Profile tab (Web UX Parity §5, Jun 2026)
 *
 * Hinge-inspired lean navigation surface matching the mobile RN app.
 * Replaces the old dashboard-style page (badges, stats grid, plans table, etc.).
 *
 * Design source of truth: /app/design_guidelines.json and
 * /app/mobile-handoff/WEB_UX_PARITY_HANDOFF.md §5.
 *
 * Structure:
 *   Top bar (logo + wordmark · help + settings)
 *   Hero (120px avatar with edit pencil, name + verified check, completeness pill)
 *   Pill tabs: Get more · Safety · My profile
 *   Tab content = clean list rows (46x46 neutral icon chip · title · subtitle · chevron)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '@/contexts/AuthContext';
import {
  CircleHelp, Settings, Pencil, BadgeCheck, Zap, Star, Activity,
  ShieldCheck, Hand, MapPin, Phone, LifeBuoy, ChevronRight,
  CalendarClock, Bell, Crown, Sparkles, IdCard,
} from 'lucide-react';
import WeeklyInsightsCard from '@/components/WeeklyInsightsCard';
import PersonaVerifyModal from '@/components/PersonaVerifyModal';

/* ---------------- tokens (mirror design_guidelines.json) ---------------- */
const INK = '#0F0F10';
const INK_SECONDARY = '#6B7280';
const SURFACE_MUTED = '#F4F5F8';
const HAIRLINE = 'rgba(15, 15, 16, 0.06)';

/* ---------------- helpers ---------------- */
function getPhotoUrl(p) {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${window.location.origin}/api/files/${p}`;
}

function initialsOf(name) {
  if (!name) return '·';
  const parts = String(name).trim().split(/\s+/);
  return (parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '');
}

/* ---------------- primitives ---------------- */
function IconChip({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="w-[46px] h-[46px] rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: SURFACE_MUTED, color: INK }}
    >
      {children}
    </div>
  );
}

function ProfileRow({ icon, title, subtitle, badge, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-3 p-4 rounded-[20px] bg-white text-left transition-colors hover:bg-[#FAFAFB] active:bg-[#F4F5F8]"
      style={{ border: `1px solid ${HAIRLINE}` }}
    >
      <IconChip>{icon}</IconChip>
      <div className="flex-1 min-w-0">
        <div
          className="text-[16px] font-[700] tracking-[-0.3px] leading-tight truncate"
          style={{ color: INK }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            className="text-[13px] font-[500] tracking-[-0.1px] mt-0.5 truncate"
            style={{ color: INK_SECONDARY }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {badge != null && (
        <span
          className="px-2 h-6 min-w-[24px] rounded-full inline-flex items-center justify-center text-[12px] font-[800] leading-none"
          style={{ backgroundColor: INK, color: '#FFFFFF' }}
          data-testid={`${testid}-badge`}
        >
          {badge}
        </span>
      )}
      <ChevronRight size={20} style={{ color: INK_SECONDARY }} className="flex-shrink-0" />
    </button>
  );
}

/* ---------------- tab strip ---------------- */
const TABS = [
  { key: 'get_more', label: 'Get more' },
  { key: 'safety', label: 'Safety' },
  { key: 'my_profile', label: 'My profile' },
];

function TabStrip({ active, onChange }) {
  return (
    <div className="flex gap-1.5 py-3" data-testid="profile-tab-strip">
      {TABS.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            data-testid={`profile-tab-${t.key.replace('_', '-')}`}
            onClick={() => onChange(t.key)}
            className="flex-1 h-10 rounded-full text-[14px] font-[800] tracking-[-0.2px] transition-colors"
            style={{
              backgroundColor: isActive ? INK : SURFACE_MUTED,
              color: isActive ? '#FFFFFF' : INK_SECONDARY,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- premium hero card ---------------- */
function PremiumHeroCard({ isPremium, onCTA }) {
  return (
    <div
      className="relative overflow-hidden rounded-[24px] mb-3"
      style={{
        backgroundColor: INK,
        padding: '44px 24px',
      }}
      data-testid="profile-premium-hero"
    >
      {/* subtle glow */}
      <div
        aria-hidden
        className="absolute -top-16 -right-10 w-48 h-48 rounded-full opacity-[0.08]"
        style={{ backgroundColor: '#D9A748', filter: 'blur(30px)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-24 -left-10 w-56 h-56 rounded-full opacity-[0.05]"
        style={{ backgroundColor: '#FFFFFF', filter: 'blur(40px)' }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Crown size={18} style={{ color: '#D9A748' }} />
          <span
            className="text-[11px] font-[800] tracking-[0.6px] uppercase"
            style={{ color: '#D9A748' }}
          >
            Meet to Keep
          </span>
        </div>
        <div
          className="text-[30px] font-[800] tracking-tight leading-none mb-2"
          style={{ color: '#FFFFFF' }}
        >
          PREMIUM
        </div>
        <div
          className="text-[14px] font-[500] leading-snug mb-6 max-w-[260px]"
          style={{ color: 'rgba(255,255,255,0.75)' }}
        >
          {isPremium
            ? 'You have unlimited likes, boosts and priority visibility.'
            : 'Unlock unlimited likes, boosts, and see who likes you.'}
        </div>
        <button
          onClick={onCTA}
          data-testid="profile-premium-cta"
          className="inline-flex items-center gap-2 h-11 px-6 rounded-full text-[14px] font-[800] tracking-[-0.2px] transition-transform hover:scale-[1.02] active:scale-[0.98]"
          style={{ backgroundColor: '#FFFFFF', color: INK }}
        >
          {isPremium ? 'Manage plan' : 'Check it out'}
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ---------------- main component ---------------- */
export default function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [inventory, setInventory] = useState({ purchased_boosts: 0, purchased_super_likes: 0 });
  const [sub, setSub] = useState(null);
  const [stats, setStats] = useState(null);
  const [callsQuota, setCallsQuota] = useState(null);
  // Dating Insights — behavioral nudges from /api/insights/nudges.
  // Used to swap the "% Right" and "Reply" stat tiles into coach CTAs
  // when their underlying rule is active for this user.
  const [nudges, setNudges] = useState([]);
  const [active, setActive] = useState('get_more');
  const [loading, setLoading] = useState(true);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [personaVerified, setPersonaVerified] = useState(false);
  // Feb 2026 · Stats range toggle. Persist choice in localStorage so the page
  // reopens on the range the user last selected.
  const [statsRange, setStatsRange] = useState(() => {
    try { return localStorage.getItem('mtk_stats_range') || 'all'; }
    catch { return 'all'; }
  });

  useEffect(() => {
    let cancel = false;
    Promise.allSettled([
      api.get('/profile'),
      api.get('/profile-score'),
      api.get('/store/inventory'),
      api.get('/subscriptions/my'),
      api.get(`/profile/stats?range=${statsRange}`),
      api.get('/calls/quota'),
      api.get('/insights/nudges'),
    ]).then((results) => {
      if (cancel) return;
      const [p, ps, inv, s, st, cq, ns] = results;
      if (p.status === 'fulfilled') setProfile(p.value.data);
      if (ps.status === 'fulfilled') setCompleteness(ps.value.data?.score ?? null);
      if (inv.status === 'fulfilled') {
        setInventory({
          purchased_boosts: Number(inv.value.data?.purchased_boosts || 0),
          purchased_super_likes: Number(inv.value.data?.purchased_super_likes || 0),
        });
      }
      if (s.status === 'fulfilled') setSub(s.value.data || null);
      if (st.status === 'fulfilled') setStats(st.value.data || null);
      if (cq.status === 'fulfilled') setCallsQuota(cq.value.data || null);
      if (ns.status === 'fulfilled') setNudges(ns.value.data?.nudges || []);
      setLoading(false);
    });
    return () => { cancel = true; };
  }, [statsRange]);

  const displayName = profile?.display_name || user?.display_name || user?.email?.split('@')[0] || 'You';
  const isVerified = Array.isArray(profile?.badges) && profile.badges.includes('selfie_verified');
  const hasIdVerified =
    personaVerified ||
    profile?.verified === true ||
    (Array.isArray(profile?.badges) && profile.badges.includes('verified'));
  const avatarPhoto =
    (Array.isArray(profile?.photos) && profile.photos[0]) || profile?.photo_url || null;
  const avatarUrl = getPhotoUrl(avatarPhoto);
  // `sub` shape from GET /subscriptions/my is `{ subscription: { tier, ... } }`.
  const tier = sub?.subscription?.tier || sub?.tier || 'free';
  const isPremium = tier === 'premium' || tier === 'vip';
  const isVip = tier === 'vip';
  // VIP coaching nudge on the Reply tile: trigger when the user has enough
  // opener signal (≥ 5 first-messages sent) AND their reply rate is under
  // 30 %. Turns the tile from a vanity metric into a one-tap coaching entry.
  const showReplyCoach =
    isVip && stats && stats.openers_sent >= 5 && stats.response_rate_pct < 30;

  // Dating Insights — behavioral nudges. When the `reply_more` /
  // `swipe_open_up` / `swipe_be_intentional` nudge is active, swap the
  // matching stat tile into a coach CTA. Same visual treatment as the VIP
  // opener-coach tile above, just wired to the insights CTA.
  const replyNudge  = nudges.find((n) => n.key === 'reply_more');
  const swipeNudge  = nudges.find(
    (n) => n.key === 'swipe_open_up' || n.key === 'swipe_be_intentional'
  );
  const showSwipeCoach = !!swipeNudge;
  const showReplyNudgeCoach = !!replyNudge;

  return (
    <div className="min-h-screen w-full bg-white" data-testid="profile-page">
      <div className="max-w-md mx-auto px-5 pb-24 pt-5">
        {/* ================= Top bar ================= */}
        <div className="flex items-center justify-between mb-6" data-testid="profile-top-bar">
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Meet to Keep"
              className="w-[30px] h-[30px] object-contain"
              data-testid="profile-logo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span
              className="text-[20px] font-[800] tracking-[-0.6px]"
              style={{ color: INK }}
            >
              Meet to Keep
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="profile-help-btn"
              onClick={() => navigate('/support')}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#F4F5F8] transition-colors"
              aria-label="Help"
            >
              <CircleHelp size={24} style={{ color: INK }} />
            </button>
            <button
              data-testid="profile-settings-btn"
              onClick={() => navigate('/settings')}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#F4F5F8] transition-colors"
              aria-label="Settings"
            >
              <Settings size={22} style={{ color: INK }} />
            </button>
          </div>
        </div>

        {/* ================= Hero ================= */}
        <div className="flex flex-col items-center mb-6" data-testid="profile-hero">
          <div className="relative" data-testid="profile-avatar-wrap">
            <div
              className="w-[120px] h-[120px] rounded-full overflow-hidden flex items-center justify-center"
              style={{ backgroundColor: SURFACE_MUTED }}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  data-testid="profile-avatar-img"
                />
              ) : (
                <span
                  className="text-[36px] font-[800] uppercase"
                  style={{ color: INK_SECONDARY }}
                  data-testid="profile-avatar-fallback"
                >
                  {initialsOf(displayName)}
                </span>
              )}
            </div>
            <button
              onClick={() => navigate('/profile/setup?edit=1')}
              data-testid="profile-avatar-edit"
              aria-label="Edit profile photo"
              className="absolute top-0 -right-2 w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
              style={{
                backgroundColor: INK,
                border: '3px solid #FFFFFF',
              }}
            >
              <Pencil size={14} color="#FFFFFF" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 mt-4" data-testid="profile-name-row">
            <span
              className="text-[26px] font-[800] tracking-[-0.6px] leading-none"
              style={{ color: INK }}
              data-testid="profile-display-name"
            >
              {displayName}
            </span>
            {isVerified && (
              <BadgeCheck
                size={22}
                className="fill-[#0F0F10] stroke-white"
                data-testid="profile-verified-check"
              />
            )}
          </div>

          {completeness != null && completeness < 100 && (
            <button
              onClick={() => navigate('/profile-score')}
              data-testid="profile-completeness-pill"
              className="mt-3 inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[12px] font-[700] tracking-[-0.1px] transition-colors hover:bg-[#F4F5F8]"
              style={{ border: `1px solid ${HAIRLINE}`, color: INK }}
            >
              Complete profile · {completeness}%
              <ChevronRight size={14} style={{ color: INK_SECONDARY }} />
            </button>
          )}
        </div>

        {/* ================= Stats row ================= */}
        {stats && (
          <>
            {/* Feb 2026 · Time-window toggle. Applies to the 5-tile headline
                row below. Persists selection to localStorage. */}
            <div className="mt-6 flex items-center justify-between" data-testid="profile-stats-range-row">
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: INK_SECONDARY }}>
                {statsRange === '7d' ? 'Last 7 days'
                  : statsRange === '30d' ? 'Last 30 days'
                  : statsRange === '90d' ? 'Last 90 days'
                  : 'Lifetime'}
              </p>
              <div className="inline-flex rounded-full bg-black/[0.04] p-0.5 border border-black/[0.06]">
                {[
                  { k: '7d',  label: '7d' },
                  { k: '30d', label: '30d' },
                  { k: '90d', label: '90d' },
                  { k: 'all', label: 'All' },
                ].map(opt => (
                  <button key={opt.k}
                          data-testid={`profile-stats-range-${opt.k}`}
                          onClick={() => {
                            setStatsRange(opt.k);
                            try { localStorage.setItem('mtk_stats_range', opt.k); } catch { /* ignore */ }
                          }}
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase transition-colors ${
                            statsRange === opt.k
                              ? 'bg-white shadow-sm text-[#0F0F10]'
                              : 'text-[#8A8A93] hover:text-[#0F0F10]'
                          }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-2 grid grid-cols-5 gap-1.5" data-testid="profile-stats-row">
            {[
              { key: 'likes_received', label: 'Likes',   value: stats.likes_received,          onClick: () => navigate('/likes') },
              { key: 'likes_sent',     label: 'Sent',    value: stats.likes_sent,              onClick: () => navigate('/matches') },
              { key: 'views',          label: 'Views',   value: stats.profile_views,           onClick: () => navigate('/likes?tab=viewers') },
              showSwipeCoach
                ? {
                    key: 'right_pct',
                    label: swipeNudge.key === 'swipe_be_intentional'
                      ? 'Slow down?'
                      : 'Try more?',
                    value: 'Insights',
                    coach: true,
                    onClick: () => navigate(swipeNudge.cta_href || '/discover'),
                  }
                : {
                    key: 'right_pct',
                    label: '% Right',
                    value: `${stats.right_swipe_pct}%`,
                    onClick: () => navigate('/scoring-detail'),
                  },
              showReplyCoach
                ? {
                    key: 'reply_rate',
                    label: 'Improve openers?',
                    value: 'Coach',
                    coach: true,
                    onClick: () => navigate('/ai-coach?starter=opener_coach'),
                  }
                : showReplyNudgeCoach
                ? {
                    key: 'reply_rate',
                    label: 'Reply more?',
                    value: 'Insights',
                    coach: true,
                    onClick: () => navigate(replyNudge.cta_href || '/messages'),
                  }
                : {
                    key: 'reply_rate',
                    label: 'Reply',
                    value: `${stats.response_rate_pct}%`,
                    onClick: () => navigate('/messages'),
                  },
            ].map((s) => (
              <button
                key={s.key}
                onClick={s.onClick}
                data-testid={`profile-stat-${s.key.replace('_', '-')}${s.coach ? '-coach' : ''}`}
                className={
                  s.coach
                    ? 'flex flex-col items-center justify-center py-3 px-1 rounded-2xl transition-transform hover:scale-[1.02] active:scale-[0.98]'
                    : 'flex flex-col items-center justify-center py-3 px-1 rounded-2xl bg-white transition-colors hover:bg-[#FAFAFB]'
                }
                style={
                  s.coach
                    ? { backgroundColor: INK, border: `1px solid ${INK}`, color: '#FFFFFF' }
                    : { border: `1px solid ${HAIRLINE}` }
                }
              >
                <span
                  className={`inline-flex items-center gap-1 text-[19px] font-[800] leading-none tracking-[-0.6px] ${
                    s.coach ? 'text-white' : ''
                  }`}
                  style={s.coach ? {} : { color: INK }}
                >
                  {s.coach && <Sparkles size={14} className="text-white" />}
                  {s.value}
                </span>
                <span
                  className="mt-1.5 text-[10.5px] font-[600] tracking-[-0.1px]"
                  style={{ color: s.coach ? 'rgba(255,255,255,0.75)' : INK_SECONDARY }}
                >
                  {s.label}
                </span>
              </button>
            ))}
          </div>
          </>
        )}

        {/* ================= Tier usage grid (Feb 2026) =================
            3-row × 2-col grid of usage cards so all six caps line up
            visually and users can budget at a glance:
              Row 1 · Likes today          | Super likes / week
              Row 2 · Boosts / week        | Intros today
              Row 3 · Voice minutes / mo   | Video minutes / mo
            Cards read from /profile/stats (swipes, super, boosts, intros)
            and /calls/quota (voice, video). Free tier boost cap is 0, so
            that cell shows the purchased_boosts stash instead. Entire
            grid taps into /subscription for a soft upsell.
            Feb 2026 — always shown for all tiers; VIP now has a finite
            intros cap (5/day) which alone justifies rendering the grid. */}
        {stats && (
          <button
            onClick={() => navigate('/subscription')}
            data-testid="profile-usage-grid"
            className="mt-3 w-full grid grid-cols-2 gap-2 rounded-2xl bg-white p-3 text-left transition-colors hover:bg-[#FAFAFB]"
            style={{ border: `1px solid ${HAIRLINE}` }}
          >
            {/* --- Row 1 --- */}
            <TierUsageCell
              label="Likes today"
              used={stats.right_swipes_today ?? 0}
              cap={stats.right_swipe_cap}
              testId="tier-usage-likes"
            />
            <TierUsageCell
              label="Super likes / week"
              used={stats.super_likes_this_week ?? 0}
              cap={stats.super_like_weekly_cap}
              accent="#B38138"
              testId="tier-usage-super"
            />
            {/* --- Row 2 --- */}
            <TierUsageCell
              label="Boosts / week"
              used={stats.boosts_this_week ?? 0}
              cap={stats.boost_weekly_cap ?? 0}
              accent="#7C3AED"
              extra={
                (stats.purchased_boosts ?? 0) > 0
                  ? `+${stats.purchased_boosts} stored`
                  : null
              }
              zeroCapFallback={
                (stats.purchased_boosts ?? 0) > 0
                  ? { value: stats.purchased_boosts, label: 'stored' }
                  : null
              }
              testId="tier-usage-boosts"
            />
            <TierUsageCell
              label="Intros today"
              used={stats.intros_sent_today ?? 0}
              cap={stats.intros_cap}
              accent="#1E4FD8"
              testId="tier-usage-intros"
            />
            {/* --- Row 3 --- */}
            <TierUsageCell
              label="Voice minutes"
              used={callsQuota?.voice?.used ?? 0}
              cap={callsQuota?.voice?.limit ?? 0}
              unlimited={callsQuota?.voice?.unlimited}
              unit="min"
              accent="#1E4FD8"
              testId="calls-usage-voice"
            />
            <TierUsageCell
              label="Video minutes"
              used={callsQuota?.video?.used ?? 0}
              cap={callsQuota?.video?.limit ?? 0}
              unlimited={callsQuota?.video?.unlimited}
              unit="min"
              accent="#7C3AED"
              testId="calls-usage-video"
            />
          </button>
        )}

        {/* Dating Insights weekly report — WoW deltas for Reply rate + Right-swipe.
            Hidden when there's no data in either window. */}
        <WeeklyInsightsCard />

        {/* ================= Tab strip ================= */}
        <TabStrip active={active} onChange={setActive} />

        {/* ================= Tab content ================= */}
        {!loading && (
          <div className="flex flex-col gap-3 mt-2">
            {active === 'get_more' && (
              <>
                <PremiumHeroCard
                  isPremium={isPremium}
                  onCTA={() => navigate('/subscription')}
                />
                <ProfileRow
                  icon={<Zap size={20} />}
                  title="Boost"
                  subtitle="Get seen by 11× more people"
                  badge={inventory.purchased_boosts > 0 ? inventory.purchased_boosts : null}
                  onClick={() => navigate('/store?highlight=boost')}
                  testid="profile-row-boost"
                />
                <ProfileRow
                  icon={<Star size={20} />}
                  title="Super Likes"
                  subtitle="2× as likely to lead to a date"
                  badge={
                    inventory.purchased_super_likes > 0 ? inventory.purchased_super_likes : null
                  }
                  onClick={() => navigate('/store?highlight=super_like')}
                  testid="profile-row-super-likes"
                />
                <ProfileRow
                  icon={<Activity size={20} />}
                  title="Check your Meet Score"
                  subtitle="See how you connect and reply"
                  onClick={() => navigate('/scoring-detail')}
                  testid="profile-row-meet-score"
                />
                <ProfileRow
                  icon={<Sparkles size={20} />}
                  title="AI Profile Coach"
                  subtitle="VIP · AI recommendations for photos, bio, prompts"
                  onClick={() => navigate('/ai-profile-coach')}
                  testid="profile-row-ai-coach"
                />
              </>
            )}

            {active === 'safety' && (
              <>
                <ProfileRow
                  icon={<ShieldCheck size={20} />}
                  title="Selfie Verification"
                  subtitle={isVerified ? "You're verified." : 'Verify to build trust'}
                  onClick={() => navigate('/verify')}
                  testid="profile-row-verification"
                />
                <ProfileRow
                  icon={<IdCard size={20} />}
                  title="Government ID Verification"
                  subtitle={
                    hasIdVerified
                      ? "You're ID-verified — blue badge active."
                      : 'Confirm your legal identity for the blue badge'
                  }
                  onClick={() => setPersonaOpen(true)}
                  testid="profile-row-persona-verify"
                />
                <ProfileRow
                  icon={<Hand size={20} />}
                  title="Block List"
                  subtitle="People you've blocked"
                  onClick={() => navigate('/settings?section=blocked')}
                  testid="profile-row-block-list"
                />
                <ProfileRow
                  icon={<MapPin size={20} />}
                  title="Safe Meet Check-Ins"
                  subtitle="Share your date plan with a trusted contact"
                  onClick={() => navigate('/safe-meet')}
                  testid="profile-row-safe-meet"
                />

                <div
                  className="mt-4 mb-1 text-[22px] font-[800] tracking-[-0.6px] leading-snug"
                  style={{ color: INK }}
                  data-testid="profile-safety-section-heading"
                >
                  Explore safety resources
                </div>

                <div className="grid grid-cols-2 gap-3" data-testid="profile-safety-pills">
                  <button
                    onClick={() => navigate('/safety/emergency')}
                    data-testid="profile-pill-crisis-hotlines"
                    className="h-14 rounded-full flex items-center justify-center gap-2 text-[14px] font-[700] tracking-[-0.2px] bg-white transition-colors hover:bg-[#FAFAFB]"
                    style={{ border: `1px solid ${HAIRLINE}`, color: INK }}
                  >
                    <Phone size={18} />
                    Crisis Hotlines
                  </button>
                  <button
                    onClick={() => navigate('/safety')}
                    data-testid="profile-pill-help-center"
                    className="h-14 rounded-full flex items-center justify-center gap-2 text-[14px] font-[700] tracking-[-0.2px] bg-white transition-colors hover:bg-[#FAFAFB]"
                    style={{ border: `1px solid ${HAIRLINE}`, color: INK }}
                  >
                    <LifeBuoy size={18} />
                    Help Center
                  </button>
                </div>
              </>
            )}

            {active === 'my_profile' && (
              <>
                <ProfileRow
                  icon={<Pencil size={18} />}
                  title="Edit Profile"
                  subtitle="Photos, bio, prompts and lifestyle"
                  onClick={() => navigate('/profile/setup?edit=1')}
                  testid="profile-row-edit-profile"
                />
                <ProfileRow
                  icon={<Sparkles size={18} />}
                  title="Your Interests"
                  subtitle="Shown publicly on your profile — tune them anytime"
                  onClick={() => navigate('/profile/setup?edit=1&section=interests')}
                  testid="profile-row-interests"
                />
                <ProfileRow
                  icon={<CalendarClock size={20} />}
                  title="Meeting Preferences"
                  subtitle="When and where you like to meet"
                  onClick={() => navigate('/meeting-prefs')}
                  testid="profile-row-meeting-prefs"
                />
                <ProfileRow
                  icon={<Bell size={20} />}
                  title="Notifications"
                  subtitle="Push, email and match alerts"
                  onClick={() => navigate('/settings#notifications')}
                  testid="profile-row-notifications"
                />
              </>
            )}
          </div>
        )}
      </div>
      <PersonaVerifyModal
        open={personaOpen}
        onClose={() => setPersonaOpen(false)}
        onVerified={() => {
          setPersonaVerified(true);
          // Best-effort profile refresh so the blue check shows immediately.
          api.get('/profile').then((r) => setProfile(r.data)).catch(() => {});
        }}
      />
    </div>
  );
}


/**
 * TierUsageCell — one cell of a tier-usage grid on the Profile page.
 *
 * Renders a bold "used / cap" number, a slim progress bar, and a tiny
 * "N left" hint. Warns red at ≤2 left so users know they're near the
 * limit. When `unlimited` is true, shows an ∞ badge instead of a bar —
 * useful for Premium's unlimited voice minutes.
 *
 * `zeroCapFallback` — when cap = 0 (e.g. free-tier boost weekly quota),
 * render a different pill entirely showing a stored-inventory count so
 * the cell isn't just an empty "0 / 0". Shape: {value, label}.
 * `extra` — small subtext under the fraction (e.g. "+3 stored" pill).
 */
function TierUsageCell({
  label, used, cap, accent = '#B91C1C', unlimited = false, unit,
  extra, zeroCapFallback, testId,
}) {
  if (unlimited) {
    return (
      <div className="min-w-0 px-2 py-1" data-testid={testId}>
        <span className="text-[10px] font-[700] uppercase tracking-[0.14em] text-[#8A8A93] block">
          {label}
        </span>
        <div className="mt-1.5 text-[17px] font-[800] leading-none tracking-[-0.4px] text-[#0F0F10]">
          ∞<span className="text-[#8A8A93] text-[11px] font-[600] ml-1">Unlimited</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full" style={{ backgroundColor: accent, opacity: 0.15 }} />
      </div>
    );
  }
  const capNum = Number(cap) || 0;
  // Free-tier fallback for a zero-cap metric (boost weekly quota on free)
  if (capNum <= 0 && zeroCapFallback) {
    return (
      <div className="min-w-0 px-2 py-1" data-testid={testId}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[10px] font-[700] uppercase tracking-[0.14em] text-[#8A8A93]">
            {label}
          </span>
          <span className="text-[11px] font-[700]" style={{ color: accent }}>
            {zeroCapFallback.label}
          </span>
        </div>
        <div className="mt-1.5 text-[17px] font-[800] tabular-nums leading-none tracking-[-0.4px] text-[#0F0F10]">
          {zeroCapFallback.value}
          <span className="text-[#8A8A93] text-[13px] font-[600]"> stored</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full" style={{ backgroundColor: accent, opacity: 0.15 }} />
      </div>
    );
  }
  // Zero cap with no fallback — render a muted "not available" cell so
  // the grid layout stays consistent instead of collapsing.
  if (capNum <= 0) {
    return (
      <div className="min-w-0 px-2 py-1" data-testid={testId}>
        <span className="text-[10px] font-[700] uppercase tracking-[0.14em] text-[#8A8A93] block">
          {label}
        </span>
        <div className="mt-1.5 text-[17px] font-[800] leading-none tracking-[-0.4px] text-[#8A8A93]">
          —<span className="text-[#8A8A93] text-[11px] font-[600] ml-1">Upgrade to unlock</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-black/[0.06]" />
      </div>
    );
  }
  const safeCap = Math.max(1, capNum);
  const safeUsed = Math.max(0, Math.min(Number(used) || 0, safeCap));
  const remaining = Math.max(0, safeCap - safeUsed);
  const pct = Math.round((safeUsed / safeCap) * 100);
  // Critical threshold scales with cap — 2 left is scary for daily likes
  // (13% of 15) but not for 300 voice minutes. Use max(2, cap*15%) instead.
  const criticalAt = Math.max(2, Math.round(safeCap * 0.15));
  const isCritical = remaining <= criticalAt;
  const barColor = isCritical ? '#DC2626' : accent;
  return (
    <div className="min-w-0 px-2 py-1" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] font-[700] uppercase tracking-[0.14em] text-[#8A8A93] truncate">
          {label}
        </span>
        <span className="text-[11px] font-[700] tabular-nums whitespace-nowrap" style={{ color: barColor }}>
          {remaining}{unit ? ` ${unit}` : ''} left
        </span>
      </div>
      <div className="mt-1.5 text-[17px] font-[800] tabular-nums leading-none tracking-[-0.4px] text-[#0F0F10]">
        {safeUsed}<span className="text-[#8A8A93] text-[13px] font-[600]"> / {safeCap}{unit ? ` ${unit}` : ''}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      {extra && (
        <div className="mt-1 text-[10px] font-[600] tracking-[-0.1px] text-[#8A8A93]" data-testid={`${testId}-extra`}>
          {extra}
        </div>
      )}
    </div>
  );
}
